import './styles.scss';

import { Button, Col, FormControl, Row } from 'react-bootstrap';
import { Datetime, getClasses, onEnter } from '@/utils';
import React, { useEffect, useRef, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';

import { DATETIME_INPUT_FORMAT } from '@/constants';
import Logger from '@/utils/logs';
import equal from 'fast-deep-equal/es6/react';

// TODO: Massive refactor needed: https://skyhop.atlassian.net/browse/SKYP-6430

// TODO: Move this to constants and import it from there.
const dayHeaders = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thurday', 'Friday', 'Saturday'];
const CURRENT_DATE = new Datetime();
export type CalendarProps = {
  name: string;
  value?: string | string[];
  selector?: dayjs.ManipulateType;
  range?: boolean;
  multiple?: boolean;
  className?: string;
  onChange?: any;
  onBlur?: any;
  disabled?: boolean;
  isValid?: boolean;
  isInvalid?: boolean;
  isDirty?: boolean;
};
export type CalendarState = {
  calendar: Dayjs[][];
  click: string;
  from: Dayjs;
  to?: Dayjs;
  selectedDays: string[];
};
const Calendar = ({
  name,
  value,
  selector = 'day',
  range = false,
  multiple = false,
  className = '',
  onChange,
  onBlur,
  disabled = false,
  isValid = false,
  isInvalid = false,
  isDirty = false,
}: CalendarProps): JSX.Element => {
  const [view, setView] = useState(CURRENT_DATE.asDayjs());
  const initCalendarState: CalendarState = {
    calendar: [],
    from: view,
    click: 'first',
    to: null,
    selectedDays:
      value && Array.isArray(value) ? value?.map((date: string): string => new Datetime(date).frontendDate) : [CURRENT_DATE.frontendDate],
  };
  const [state, setState] = useState(initCalendarState);
  const { from, calendar, to, click, selectedDays } = state;
  const calendarRef = useRef<HTMLDivElement>(null);
  const lastSelectedDays = useRef<string[]>([]);

  // Focus the element when the component mounts
  useEffect((): void => {
    calendarRef.current?.focus();
  }, []);

  useEffect((): void => {
    const result = value ? (Array.isArray(value) ? dayjs(value?.[0]) : dayjs(value as string)) : CURRENT_DATE.asDayjs();
    setView(result);
  }, [value]);

  // Set Calendar Display
  useEffect((): void => {
    const weeks = [];
    const startDay = view.startOf('month').startOf('week');
    // TODO: Remove this if it is not needed.
    // const endDay = from.endOf('month').endOf('week');
    const day = new Datetime(startDay).subtract(1, 'day');

    for (let i = 1; i <= 5; i++) {
      weeks.push(
        Array(7)
          .fill(0)
          .map((): Dayjs => day.add(1, 'day').asDayjs())
      );
    }
    setState((current: CalendarState): CalendarState => ({ ...current, calendar: weeks }));
    // TODO: Add remaining dependencies, and ensure they do not cause excess re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view.get('month')]);

  useEffect((): void => {
    if (selector === 'day' && multiple === false && range === false) {
      onChange({ target: { name, value: new Datetime(from).dateInput } });
    }
  }, [from]);

  // Set 'to' to a dayjs value if 'range' is true
  useEffect((): void => {
    if (!range) return;
    setState((current: CalendarState): CalendarState => ({ ...current, to: from.clone().add(2, 'day') }));
    // TODO: Add remaining dependencies, and ensure they do not cause excess re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range]);
  // Call onChange when selectedDays changes
  useEffect(() => {
    if (equal(selectedDays, lastSelectedDays.current)) return;
    lastSelectedDays.current = selectedDays;
    onChange({ target: { name, value: selectedDays } });
    // TODO: Add remaining dependencies, and ensure they do not cause excess re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDays]);

  // Helper Funcs
  // check if day is the same as start or end date
  // TODO: These method are all being re-declared eon every render; move them into a custom hook as proper callbacks.
  const isSelected = (day: Dayjs): boolean => {
    if (multiple) return state.selectedDays.some((d: string): boolean => new Datetime(d).asDayjs().isSame(day, 'day'));
    if (range) return from.isSame(day, 'day') || to.isSame(day, 'day');
    return selectedDays.some((d: string): boolean => day.isSame(d));
  };
  // check if day is before start month
  const beforeMonth = (day: Dayjs): boolean => {
    return day.isBefore(view.startOf('month'), 'day');
  };
  // check if day is after start month
  const afterMonth = (day: Dayjs): boolean => {
    return day.isAfter(view.endOf('month'), 'day');
  };
  // check if day is today
  const isToday = (day: Dayjs): boolean => {
    return day.isSame(CURRENT_DATE.dateInput, 'day');
  };
  // check if day is same as the start date of selector
  const isStart = (day: Dayjs): boolean => {
    return day.isSame(from.startOf(selector), 'day');
  };
  // check if day is same as the end date of selector
  const isEnd = (day: Dayjs): boolean => {
    return day.isSame(from.endOf(selector), 'day');
  };
  // check if day is between start date and end date of selector
  const isBetween = (day: Dayjs, start: Dayjs, end: Dayjs): boolean => {
    return day.isAfter(start.startOf(selector), 'day') && day.isBefore(end.endOf(selector), 'day');
  };
  // check if day is within start date and end date
  const isWithin = (day: Dayjs, start: Dayjs, end: Dayjs): boolean => {
    return day.isAfter(start) && day.isBefore(end);
  };

  const getDayStyles = (day: Dayjs): string => {
    switch (selector) {
      case 'week':
      case 'month':
        if (isStart(day)) return 'range-start';
        if (isEnd(day)) return 'range-end';
        if (isBetween(day, from, from)) return 'range';
      case 'day':
        if (isSelected(day)) return 'selected';
      default:
        if (beforeMonth(day)) return 'before';
        if (afterMonth(day)) return 'before';
        if (range && isWithin(day, from, to)) return 'range';
        if (isToday(day)) return 'today';
        return '';
    }
  };

  const handleOnBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    // TODO: Fix memory leak: add a cleanup method for this timeout.
    setTimeout(() => {
      // Check if the currently focused element is not nested inside calendar
      if (calendarRef.current && !calendarRef.current.contains(document.activeElement)) {
        if (onBlur) onBlur(event);
      }
    }, 100);
  };

  // state handlers
  const handleOnChange =
    (day: Dayjs): (() => void) =>
    (): void => {
      if (disabled) return;
      if (multiple) {
        setState((current: CalendarState): CalendarState => {
          const alreadySelected = current.selectedDays.some((d: string): boolean => new Datetime(d).asDayjs().isSame(day, 'day'));
          if (alreadySelected) {
            // if the day is already selected, remove it from the selectedDays list
            return {
              ...current,
              selectedDays: current.selectedDays.filter((d: string): boolean => !new Datetime(d).asDayjs().isSame(day, 'day')),
            };
          } else {
            // otherwise, add it to the selectedDays list
            return { ...current, selectedDays: [...current.selectedDays, day.format(DATETIME_INPUT_FORMAT)] };
          }
        });
      }
      if (range) {
        let key: string;
        let clickValue: string;
        // set up state machine
        switch (click) {
          case 'first':
            // Start changing value
            key = 'from';
            clickValue = 'second';
            if (day.isAfter(to)) {
              // since we are updating the opposite key we want to start the user in the same state
              key = 'to';
              clickValue = 'first';
            }
            setState((current: CalendarState): CalendarState => ({ ...current, [key]: day, click: clickValue }));
            break;
          case 'second':
            // Start changing value
            key = 'to';
            clickValue = 'first';
            if (day.isBefore(from)) {
              // since we are updating the opposite key we want to start the user in the same state
              key = 'from';
              clickValue = 'second';
            }
            setState((current: CalendarState): CalendarState => ({ ...current, [key]: day, click: clickValue }));
            break;
          default:
            Logger.of('Calendar').error('onChange: click is in an invalid state');
            break;
        }
      }
      if (!range && !multiple) {
        setState((current: CalendarState): CalendarState => ({ ...current, from: day }));
      }
    };
  const onSetValue = (unit: dayjs.UnitType, value: number): void => {
    setState((current: CalendarState): CalendarState => {
      const output = {
        ...current,
        from: current.from.set(unit, value),
      };
      if (range) {
        output.to = output.from.add(2, 'day');
        output.click = 'first';
      }
      return output;
    });
  };
  const onSubtractMonth = (value: number): void => {
    setView((current) => current.subtract(value, 'month'));
  };
  const onAddMonth = (value: number): void => {
    setView((current) => current.add(value, 'month'));
  };
  const handleSetYear = (event: any): void => {
    const { value } = event.target;
    onSetValue('year', parseInt(value));
  };

  const lastValue = useRef(value || []);
  useEffect(() => {
    if (equal(value || [], lastValue.current || [])) return;
    lastValue.current = value;
    setState((current) => ({
      ...current,
      selectedDays:
        value && Array.isArray(value) ? value?.map((date: string): string => new Datetime(date).frontendDate) : [CURRENT_DATE.frontendDate],
    }));
  }, [value]);

  return (
    <section
      className={getClasses(
        'Calendar',
        isDirty === true ? 'is-dirty' : undefined,
        isValid === true ? 'is-valid' : undefined,
        isInvalid === true ? 'is-invalid' : undefined,
        className
      )}
      tabIndex={-1}
      onBlur={handleOnBlur}
      ref={calendarRef}
    >
      <div>
        <Row className="Calendar-Header">
          <div className="d-flex align-items-center justify-content-between">
            <div>
              {/* TODO: Possibly make the month display a dropdown for quicker selection. */}
              {view.format('MMMM')}
              <span className="ms-2">
                <FormControl
                  maxLength={4}
                  tabIndex={-1}
                  className="text-center {width:50px!;padding:0.2rem|0!;}"
                  defaultValue={view.format('YYYY')}
                  onKeyDown={onEnter(handleSetYear)}
                  onBlur={handleSetYear}
                  key={view.format('YYYY')}
                />
              </span>
            </div>
            <div className="d-flex gap-2">
              <Button variant="icon" size="sm" onClick={(): void => onSubtractMonth(1)} tabIndex={-1}>
                <i className="sv sv-chevron-left" />
              </Button>
              <Button variant="icon" size="sm" onClick={(): void => onAddMonth(1)} tabIndex={-1}>
                <i className="sv sv-chevron-right" />
              </Button>
            </div>
          </div>
        </Row>
        <Row className="Calendar-Body">
          <Col className="Calendar-Date">
            <Row xs={7} className="">
              {dayHeaders.map(
                (dayHeader: string, index: number): JSX.Element => (
                  <Col key={`${dayHeader}_${index}`} className="d-flex justify-content-center">
                    {dayHeader[0]}
                  </Col>
                )
              )}
            </Row>
            {calendar.map(
              (week: Dayjs[], weekIndex: number): JSX.Element => (
                <Row key={`week_${weekIndex}`} xs={7} className="">
                  {week.map(
                    (day: Dayjs, dayIndex: number): JSX.Element => (
                      <Col key={`${week}_${day}`} className="d-flex justify-content-center" onClick={handleOnChange(day)}>
                        <div key={`${week}_${day}_${dayIndex}`} className={`${getDayStyles(day)} day`}>
                          {day.format('D').toString()}
                        </div>
                      </Col>
                    )
                  )}
                </Row>
              )
            )}
          </Col>
        </Row>
      </div>
    </section>
  );
};

export default Calendar;
